import { dataUrl } from "common/infrastructure/endpoints";
import { DeviceType, dataInterval, Interval } from "common/types";
import { t } from "i18next";
import { get } from "../../common/infrastructure/requests";
import moment from "moment";
import { actionCreator } from "nodeDetails/nodeDetails.reducer";
import {
  DateToUnixConverter,
  UnixToDateConverter,
} from "../../common/DateConverter";
import i18n from "../../common/languageHandling/i18n";
import {
  Grouping,
  IInstallation,
  IInstallationData,
  IInstallationDataResult,
  IInstallationExtended,
  ISelectedInstallation,
  IUnixTimeObject,
  IUtility,
  IValues,
  PeriodType,
  UtlType,
} from "../../nodeDetails/types";
import { getDaysInCurrentMonth, getOneIntervalLater } from "./dateFunctions";
import { IMeasuringPoint } from "safetySensors/types";

export const getSumOfAllMeasuringPoints = (data: IInstallationData[]) => {
  if (data.length === 1) {
    return data[0].Result;
  }
  const allValues = [];

  let result: IInstallationDataResult[] = [];

  if (
    [UtlType.HeatCostAllocator, UtlType.HeatCostAllocatorE].includes(
      data[0].Result[0].Utl
    )
  ) {
    allValues.push(mergeMultipleDatasets(data, 0));
    allValues.push(mergeMultipleDatasets(data, 1));
  } else {
    allValues.push(mergeMultipleDatasets(data, 0));
    allValues.push(mergeMultipleDatasets(data, 1));
    allValues.push(mergeMultipleDatasets(data, 2));
  }

  for (let i = 0; i < allValues.length; i++)
    result.push({
      Func: data[0].Result[i].Func,
      Unit: data[0].Result[i].Unit,
      Utl: data[0].Result[i].Utl,
      Values: allValues[i],
    });
  return result;
};

export const mergeMultipleDatasets = (
  data: IInstallationData[],
  func: number
) => {
  let returnArray: IValues[] = [];
  data
    .map((d) => d.Result[func].Values)
    .forEach((v, i) =>
      v.forEach((v, i) => {
        returnArray[i]
          ? (returnArray[i] = {
              Time: v.Time,
              Value: returnArray[i]
                ? (returnArray[i].Value += v.Value || 0)
                : (returnArray[i].Value = v.Value || 0),
            })
          : returnArray.push({
              Time: v.Time,
              Value: v.Value || 0,
            });
      })
    );
  return returnArray;
};

export const convertDataResultGToKg = (result: IInstallationDataResult) => {
  result = {
    Func: result.Func,
    Utl: result.Utl,
    Unit: "kg",
    Values: [
      ...result.Values.map((value) => ({
        ...value,
        Value: convertGToKg(value.Value),
      })),
    ],
  };
  return result;
};
export const convertDataGToKg = (data: IInstallationData) => {
  data = {
    ID: data.ID,
    Name: data.Name,
    Result: [
      data.Result[0],
      data.Result[1],
      convertDataResultGToKg(data.Result[2]),
    ],
  };
  return data;
};
export const convertGToKg = (g: number) => {
  return g / 1000;
};

export const convertConsumptionData = (data: IInstallationData) => {
  return data.Result.map((res) => {
    const shouldConvertToLitre = res.Unit === "m3" && res.Func !== "price";

    const returnData: IInstallationDataResult = {
      ...res,
      Unit: shouldConvertToLitre ? i18n.t("litres") : res.Unit,
      Values: [
        ...res.Values.map((value) => ({
          ...value,
          Value: shouldConvertToLitre
            ? convertCubicMetersToLitres(value.Value)
            : value.Value,
          ConvertedTime: UnixToDateConverter(value.Time),
        })),
      ],
    };
    return returnData;
  });
};

const convertCubicMetersToLitres = (cubicMeterValue: number): number => {
  return Math.round(cubicMeterValue * 1000);
};

export const getMaxTemp = (data: IInstallationDataResult[]) => {
  if (data.length > 0) {
    if (data[0].Utl === UtlType.Temperature) {
      return Math.max(
        ...data[0].Values.map((v) => v.Value).filter((v) => v)
      ).toFixed(1);
    }
  }
};
export const getMinTemp = (data: IInstallationDataResult[]) => {
  if (data.length > 0) {
    if (data[0].Utl === UtlType.Temperature) {
      return Math.min(
        ...data[0].Values.map((v) => v.Value).filter((v) => v)
      ).toFixed(1);
    }
  }
};

export const getMaxFromDataset = (dataset: IInstallationDataResult) => {
  return Math.max(...dataset.Values.map((v) => v.Value).filter((v) => v));
};

export const calculatePeriodAverage = (data: IValues[]) => {
  const totalSum = getSumOfDataset(data);
  const dataEntries = data.filter((i) => i.Value).length;
  if (!dataEntries) return 0;

  return +(totalSum / dataEntries).toFixed(1);
};
export const calculateDailyAverage = (data: IValues[], period: PeriodType) => {
  switch (period) {
    case "week":
    case "month":
      return calculatePeriodAverage(data);
    case "year":
      return (
        data.reduce(
          (prevValue, currentValue) =>
            prevValue +
            currentValue.Value / +moment.unix(currentValue.Time).daysInMonth(),

          0
        ) / data.filter((v) => v.Value).length
      );
    case "days":
      return 0;
  }
};

export const getDateYearsPrior = (date: number, yearsBack: number) => {
  return DateToUnixConverter(moment.unix(date).subtract(yearsBack, "year"));
};
export const subtractYearFromUnix = (date: number) => {
  return DateToUnixConverter(moment.unix(date).subtract(1, "year"));
};

export const isInstallationSensor = (installation: IInstallation) => {
  return (
    installation.DeviceType === DeviceType.Smoke ||
    installation.DeviceType === DeviceType.Leakage ||
    installation.DeviceType === DeviceType.ShellProtection
  );
};

export const getDataIntervalOnString = (interval: string): dataInterval => {
  switch (interval) {
    case t("Year"):
      return dataInterval.Month;
    case t("Month"):
      return dataInterval.Day;
    case t("Week"):
      return dataInterval.Day;
    case t("Day"):
      return dataInterval.Hour;
    default:
      return dataInterval.Month;
  }
};

export const getUtilityOnDeviceType = (deviceType: DeviceType): IUtility[] => {
  switch (deviceType) {
    case DeviceType.ColdWater:
      return [
        { utlType: UtlType.ColdWater, aggregate: "con" },
        { utlType: UtlType.ColdWater, aggregate: "price" },
        { utlType: UtlType.ColdWater, aggregate: "co2" },
      ];
    case DeviceType.HotWater:
      return [
        { utlType: UtlType.HotWater, aggregate: "con" },
        { utlType: UtlType.HotWater, aggregate: "price" },
        { utlType: UtlType.HotWater, aggregate: "co2" },
      ];
    case DeviceType.Electric:
      return [
        { utlType: UtlType.Electricity, aggregate: "con" },
        { utlType: UtlType.Electricity, aggregate: "price" },
        { utlType: UtlType.Electricity, aggregate: "co2" },
      ];
    case DeviceType.HeatEnergy:
      return [
        { utlType: UtlType.HeatEnergy, aggregate: "con" },
        { utlType: UtlType.HeatEnergy, aggregate: "price" },
        { utlType: UtlType.HeatEnergy, aggregate: "co2" },
      ];
    case DeviceType.Climate:
      return [
        { utlType: UtlType.Temperature, aggregate: "avg" },
        { utlType: UtlType.RelativeHumidity, aggregate: "avg" },
      ];

    case DeviceType.HeatCostAllocator:
      return [
        { utlType: UtlType.HeatCostAllocator, aggregate: "con" },
        { utlType: UtlType.HeatCostAllocator, aggregate: "price" },
      ];

    default:
      console.error("missing deviceType: ", deviceType);
      return [{ utlType: UtlType.Undefined, aggregate: "" }];
  }
};

export const identifyDeviceTypeOnWater = (
  installation: IInstallationExtended | IInstallation
) => {
  const registerCodes = installation.Registers.map((r) => r.UtilityCode);
  if (registerCodes.includes(UtlType.ColdWater)) return DeviceType.ColdWater;
  else return DeviceType.HotWater;
};

export const utlAndAggregateOnUtlTypeWithPriceAndCo2 = (
  array: IUtility[],
  utlType: UtlType
) => {
  switch (utlType) {
    case UtlType.ColdWater:
    case UtlType.Electricity:
    case UtlType.HeatEnergy:
    case UtlType.HotWater: {
      array.push({
        utlType: utlType,
        aggregate: "con",
      });
      array.push({
        utlType: utlType,
        aggregate: "price",
      });
      array.push({
        utlType: utlType,
        aggregate: "co2",
      });
      break;
    }

    case UtlType.HeatCostAllocator:
      array.push({
        utlType: UtlType.HeatCostAllocator,
        aggregate: "con",
      });
      array.push({
        utlType: UtlType.HeatCostAllocator,
        aggregate: "price",
      });
      break;
    case UtlType.HeatCostAllocatorE:
      array.push({
        utlType: UtlType.HeatCostAllocatorE,
        aggregate: "con",
      });
      array.push({
        utlType: UtlType.HeatCostAllocatorE,
        aggregate: "price",
      });
      break;

    case UtlType.Temperature:
      array.push({
        utlType: UtlType.Temperature,
        aggregate: "avg",
      });
      break;
    case UtlType.RelativeHumidity:
      array.push({
        utlType: UtlType.RelativeHumidity,
        aggregate: "avg",
      });
      break;
    default:
      break;
  }

  return array;
};
export const utlAndAggregateOnUtlType = (
  array: IUtility[],
  utlType: UtlType
) => {
  switch (utlType) {
    case UtlType.ColdWater:
    case UtlType.Electricity:
    case UtlType.HeatEnergy:
    case UtlType.HotWater:
      array.push({
        utlType: utlType,
        aggregate: "con",
      });
      break;

    case UtlType.HeatCostAllocator:
      array.push({
        utlType: utlType,
        aggregate: "con",
      });
      break;
    case UtlType.HeatCostAllocatorE:
      array.push({
        utlType: utlType,
        aggregate: "con",
      });
      break;
  }
  return array;
};

export const getSumOfDataset = (dataset: IValues[]) => {
  if (!dataset.filter((d) => d.Value).length) return 0;
  return dataset
    .map((data) => data.Value)
    .reduce((prevValue, currentValue) => {
      return prevValue + currentValue;
    });
};

export const capitalizeFirstLetter = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export const getConsumptionSelectedInstallation = (
  data: IInstallationDataResult[],
  displayName: string,
  measuringPoints: IMeasuringPoint[],
  period: PeriodType,
  comparisonData?: IInstallationDataResult[]
) => {
  if (!data.length) {
    const setSelectedInstallation: ISelectedInstallation = {
      displayName,
      detailedInstallation: {
        data: [],
        totalConsumption: null,
        averageConsumption: null,
        averageRelativeHumidity: null,
        averageTemperature: null,
        cost: null,
        emissions: null,
        previousData: comparisonData || [],
      },
      measuringPoints,
    };
    return setSelectedInstallation;
  }
  let averageConsumption = calculateDailyAverage(data[0]?.Values ?? [], period);
  if (isNaN(averageConsumption)) averageConsumption = 0;
  const cost = getSumOfDataset(data[1]?.Values ?? []);
  const emission = getSumOfDataset(data[2]?.Values ?? []);
  const setSelectedInstallation: ISelectedInstallation = {
    displayName,
    detailedInstallation: {
      data: [data[0], data[1], data[2]],
      totalConsumption: +getSumOfDataset(data[0].Values).toFixed(2),
      averageConsumption: +averageConsumption.toFixed(1),
      averageRelativeHumidity: null,
      averageTemperature: null,
      cost: +cost.toFixed(1),
      emissions: +emission.toFixed(1),
      previousData: comparisonData || [],
    },
    measuringPoints: measuringPoints,
  };
  return setSelectedInstallation;
};

export const calculateMonthCostAndEmission = (
  installations: IInstallationExtended[]
) => {
  const consumptionInstallations = installations.filter(
    (i) =>
      i.DeviceType !== DeviceType.Leakage &&
      i.DeviceType !== DeviceType.Smoke &&
      i.DeviceType !== DeviceType.ShellProtection &&
      i.DeviceType !== DeviceType.Climate
  );
  let totalMonthCost = 0;
  let totalMonthEmission = 0;
  consumptionInstallations.forEach((i) => {
    totalMonthCost += i.currentMonthCost;
    totalMonthEmission += i.currentMonthEmission;
  });
  totalMonthCost = +totalMonthCost.toFixed(1);
  totalMonthEmission = +(totalMonthEmission / 1000).toFixed(1);

  return { totalMonthCost, totalMonthEmission };
};

export const getClimateSelectedInstallation = (
  data: IInstallationDataResult[],
  displayName: string,
  currency?: string
) => {
  if (data.length > 1) {
    const temperature = data[0].Utl === "T" ? data[0].Values : data[1].Values;
    const averageTemperature = calculatePeriodAverage(temperature);
    const relativeHumidity =
      data[0].Utl === "RH" ? data[0].Values : data[1].Values;
    const averageRelativeHumidity = calculatePeriodAverage(relativeHumidity);

    const selectedInstallation: ISelectedInstallation = {
      displayName: displayName,
      detailedInstallation: {
        data: [...data],
        averageTemperature: averageTemperature,
        averageRelativeHumidity: averageRelativeHumidity,
        averageConsumption: null,
        cost: null,
        emissions: null,
        totalConsumption: null,
        previousData: [],
      },
      measuringPoints: [],
    };
    return selectedInstallation;
  } else if (!data.length) {
    const selectedInstallation: ISelectedInstallation = {
      displayName: displayName,
      detailedInstallation: {
        data: [],
        averageTemperature: null,
        averageRelativeHumidity: null,
        averageConsumption: null,
        cost: null,
        emissions: null,
        totalConsumption: null,
        previousData: [],
      },
      measuringPoints: [],
    };
    return selectedInstallation;
  } else {
    const temperature = data[0].Values;
    const averageTemperature = calculatePeriodAverage(temperature);
    const selectedInstallation: ISelectedInstallation = {
      displayName: displayName,
      detailedInstallation: {
        data: [...data],
        averageTemperature: averageTemperature,
        averageRelativeHumidity: null,
        averageConsumption: null,
        cost: null,
        emissions: null,
        totalConsumption: null,
        previousData: [],
      },
      measuringPoints: [],
    };
    return selectedInstallation;
  }
};

export const getDisplayNameOnDeviceType = (deviceType: DeviceType) => {
  switch (deviceType) {
    case DeviceType.Climate:
      return t("Indoor climate");
    case DeviceType.ColdWater:
      return t("Cold water");
    case DeviceType.Electric:
      return t("Electricity");
    case DeviceType.HotWater:
      return t("Hot water");
    case DeviceType.HeatEnergy:
      return t("Heat");
    case DeviceType.HeatCostAllocator:
      return t("Heat Cost Allocator");
    default:
      return "";
  }
};

export const getDataForPrognosis = (installation: IInstallationExtended) => {
  let currentData: IValues[] = installation.data[0].Values;
  let comparisonData: IValues[] = installation.comparisonData
    ? installation.comparisonData[0].Values
    : [];
  return { currentData, comparisonData };
};

export const groupInstallations = (installations: IInstallationExtended[]) => {
  const groupedInstallation: IInstallationExtended[] = [];
  installations.forEach((i) => {
    if (groupedInstallation.find((g) => g.DeviceType === i.DeviceType)) return;
    groupedInstallation.push(i);
  });
  return groupedInstallation;
};

export const getDeviceTypeOnDisplayName = (displayName: string): DeviceType => {
  switch (displayName) {
    case t("Indoor climate"):
      return DeviceType.Climate;
    case t("Cold water"):
      return DeviceType.ColdWater;
    case t("Electricity"):
      return DeviceType.Electric;
    case t("Hot water"):
      return DeviceType.HotWater;
    case t("Heat cost allocator"):
      return DeviceType.HeatCostAllocator;
    default:
      return DeviceType.HotWater;
  }
};

export const getPrognosis = (
  dataSet: IValues[],
  comparisonDataset: IValues[]
) => {
  let prognosis;
  const date = moment().date();
  if (date < 3 && comparisonDataset) {
    prognosis = getSumOfDataset(comparisonDataset);
  } else {
    const currentDataSum = getSumOfDataset(dataSet);
    const averageConsumption = calculatePeriodAverage(dataSet);
    const remainingDaysOfMonth = getDaysInCurrentMonth() - (date - 1);
    prognosis = currentDataSum + averageConsumption * remainingDaysOfMonth;
  }
  return +prognosis.toFixed(0);
};

export const getLatestRealValueForIndoorClimate = (
  data: IInstallationDataResult[]
) => {
  const realValues = data
    .find((r) => r.Func === "avg")
    ?.Values.filter((v) => v.Value);
  if (realValues) {
    return realValues.find(
      (v) => v.Time === Math.max(...realValues?.map((v) => v.Time))
    );
  }
  return undefined;
};

export const getFirstRealValue = (data: IValues[]) => {
  const realValues = data.filter((v) => v.Value);
  if (realValues)
    return realValues.find(
      (v) => v.Time === Math.min(...realValues.map((v) => v.Time))
    );
};

export const adjustVisibleData = (
  data: IInstallationDataResult[],
  newLength: number
) => {
  const returnArray: IInstallationDataResult[] = [];
  data.forEach((installation) => {
    const currentLength = installation.Values.length;
    const newValues: IValues[] = [];
    for (let i = 0; i < newLength - currentLength; i++) {
      newValues.push({
        Time: 0,
        Value: 0,
      });
    }
    installation.Values.forEach((v) => newValues.push(v));
    returnArray.push({
      Func: installation.Func,
      Unit: installation.Unit,
      Utl: installation.Utl,
      Values: newValues,
    });
  });
  return returnArray;
};

export const expectedLengthOfDataset = (unixTimeObject: IUnixTimeObject) => {
  switch (unixTimeObject.period) {
    case "days":
      return 24;
    case "week":
      return 7;
    case "year":
      return 12;
    case "month":
      return moment.unix(unixTimeObject.from).daysInMonth();
    default:
      return moment.unix(unixTimeObject.from).daysInMonth();
  }
};

export const sortDataResults = (data: IInstallationData[]) => {
  const sortedData = data.map((d) => {
    const consumptionData = d.Result.find((r) => r.Func === "con");
    const co2Data = d.Result.find((r) => r.Func === "co2");
    const priceData = d.Result.find((r) => r.Func === "price");
    if (!consumptionData || !co2Data || !priceData) {
      return { ...d };
    }
    return {
      ID: d.ID,
      Name: d.Name,
      Result: [consumptionData, priceData, co2Data],
    };
  });
  return sortedData;
};

export const convertDataIfFromDateNotAllowed = (
  data: IInstallationData,
  unixTimeObject: IUnixTimeObject
) => {
  const expectedDataPoints = expectedLengthOfDataset(unixTimeObject);
  const receivedDataPoints = data.Result[0].Values.length;
  if (receivedDataPoints < expectedDataPoints) {
    data = {
      ID: data.ID,
      Name: data.Name,
      Result: adjustVisibleData(data.Result, expectedDataPoints),
    };
  }
  return data;
};

export const convertIfUnitM3 = (data: IInstallationData) => {
  if (
    data.Result.find(
      (r) => r.Utl === UtlType.ColdWater || r.Utl === UtlType.HotWater
    )
  )
    data = {
      ID: data.ID,
      Name: data.Name,
      Result: convertConsumptionData(data),
    };
  return data;
};

export const setSelectedInstallationWithEmptyData =
  (
    deviceType: DeviceType,
    unixTimeObject: IUnixTimeObject,
    displayName: string,
    yearsBack: number,
    domain: string,
    nodeId: number,
    interval: Interval,
    utlAndAggregate: IUtility[]
  ) =>
  async (dispatch: ({}) => void) => {
    if (deviceType === DeviceType.HeatCostAllocator) {
      const selectedHCAInstallation = getConsumptionSelectedInstallation(
        [],
        displayName,
        [],
        unixTimeObject.period
      );
      dispatch(actionCreator.setSelectedInstallation(selectedHCAInstallation));
      return;
    } else if (deviceType === DeviceType.Climate) {
      const selectedClimateInstallation = getClimateSelectedInstallation(
        [],
        displayName
      );
      dispatch(
        actionCreator.setSelectedInstallation(selectedClimateInstallation)
      );
      return;
    } else {
      let comparisonFrom = getDateYearsPrior(unixTimeObject.from, yearsBack);
      const comparisonTo = getDateYearsPrior(
        getOneIntervalLater(unixTimeObject.from, unixTimeObject.period),
        yearsBack
      );
      let comparisonData = await get<IInstallationData[]>(
        dataUrl(
          domain,
          nodeId,
          comparisonFrom,
          comparisonTo,
          interval,
          utlAndAggregate,
          Grouping.Apartment
        )
      );
      if (!comparisonData.length) {
        const setSelectedInstallation: ISelectedInstallation =
          getConsumptionSelectedInstallation(
            [],
            displayName,
            [],
            unixTimeObject.period
          );
        dispatch(
          actionCreator.setSelectedInstallation(setSelectedInstallation)
        );
        return;
      } else {
        comparisonData[0] = convertDataIfFromDateNotAllowed(
          comparisonData[0],
          unixTimeObject
        );
        comparisonData[0] = convertIfUnitM3(comparisonData[0]);
        const setSelectedInstallation: ISelectedInstallation =
          getConsumptionSelectedInstallation(
            [],
            displayName,
            [],
            unixTimeObject.period,
            comparisonData[0].Result
          );
        dispatch(
          actionCreator.setSelectedInstallation(setSelectedInstallation)
        );
        return;
      }
    }
  };

export const setUtility = (
  installation: IInstallation,
  callback: (array: IUtility[], utlType: UtlType) => IUtility[]
) => {
  let requestedData: IUtility[] = [];
  installation.Registers.map((r) => {
    requestedData = callback(requestedData, r.UtilityCode);
  });

  return requestedData;
};
